import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IGroup } from '@interfaces/IGroup';
import { IAppState } from '@myStore/app.state';
import {
  ActivateGroupFailure,
  ActivateGroupRequest,
  ActivateGroupSuccess,
  AddAdminsToTGGroupFailure,
  AddAdminsToTGGroupRequest,
  AddAdminsToTGGroupSuccess,
  AddCheckingTeacherFailure,
  AddCheckingTeacherRequest,
  AddCheckingTeacherSuccess,
  DeleteCheckingTeacherFailure,
  DeleteCheckingTeacherRequest,
  DeleteCheckingTeacherSuccess,
  DeleteGroupFailure,
  DeleteGroupRequest,
  DeleteGroupSuccess,
  DeleteTeacherFromGroupFailure,
  DeleteTeacherFromGroupRequest,
  DeleteTeacherFromGroupSuccess,
  DeleteTeacherFromSelectedGroupsFailure,
  DeleteTeacherFromSelectedGroupsRequest,
  DeleteTeacherFromSelectedGroupsSuccess,
  DeleteTeacherRequest,
  EditStudentAccessFailure,
  EditStudentAccessRequest,
  EditStudentAccessSuccess,
  EGroupsChangesActions,
  GroupsStudentHappyPointAllowedFailure,
  GroupsStudentHappyPointAllowedRequest,
  GroupsStudentHappyPointAllowedSuccess,
  RefreshCheckingTeacherHomeworksFailure,
  RefreshCheckingTeacherHomeworksRequest,
  RefreshCheckingTeacherHomeworksSuccess,
  ReturnTeacherFailure,
  ReturnTeacherRequest,
  ReturnTeacherSuccess,
  SendActivateStudentToGroupFailure,
  SendActivateStudentToGroupRequest,
  SendActivateStudentToGroupSuccess,
  SendAddStudentsToGroupFailure,
  SendAddStudentsToGroupRequest,
  SendAddStudentsToGroupSuccess,
  SendAddTeachersToGroupFailure,
  SendAddTeachersToGroupRequest,
  SendAddTeachersToGroupSuccess,
  SendCreateGroupFailure,
  SendCreateGroupRequest,
  SendCreateGroupSuccess,
  SendDeactivateGroupFailure,
  SendDeactivateGroupRequest,
  SendDeactivateGroupSuccess,
  SendStudentDropoutFromGroupFailure,
  SendStudentDropoutFromGroupRequest,
  SendStudentDropoutFromGroupSuccess,
  SendTransfersStudentsToGroupFailure,
  SendTransfersStudentsToGroupRequest,
  SendTransfersStudentsToGroupSuccess,
  SendUpdateGroupFailure,
  SendUpdateGroupRequest,
  SendUpdateGroupSuccess,
  SetStudentOnlineInGroupFailure,
  SetStudentOnlineInGroupRequest,
  SetStudentOnlineInGroupSuccess,
  TransferToESDPFailure,
  TransferToESDPRequest,
  TransferToESDPSuccess,
} from '@myStore/groups-changes/groups-changes.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { GroupsChangesService } from '@services/groups-changes/groups-changes.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, switchMap } from 'rxjs/operators';
import { FormDialogComponent } from '@shared/components/UI/form-dialog/form-dialog.component';
import { PeriodFormComponent } from '@shared/components/forms/period-form/period-form.component';
import { MatDialog } from '@angular/material/dialog';

@Injectable()
export class GroupsChangesEffects {
  constructor(
    private _actions$: Actions,
    private _store: Store<IAppState>,
    private _groupsChangesService: GroupsChangesService,
    private _router: Router,
    private _toastr: ToastrService,
    private _dialog: MatDialog,
    private _location: Location
  ) {}

  $sendCreateGroup = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SendCreateGroupRequest>(
          EGroupsChangesActions.SendCreateGroupRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService.create(action.data).pipe(
            map((group: IGroup) => {
              this._location.go('/courses-and-groups');
              this._toastr.success(
                `Группа ${group.full_name} успешно создана!`,
                '',
                {
                  timeOut: 3500,
                }
              );
              if (group.id) {
                this._dialog.open(FormDialogComponent, {
                  width: '450px',
                  data: {
                    title: 'Создать период',
                    content: PeriodFormComponent,
                    groupId: group?.id,
                  },
                });
              }
              this._store.dispatch(new SendCreateGroupSuccess());
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При создании группы возникла ошибка'
                }`,
                'Что-то пошло не так',
                {
                  timeOut: 3500,
                }
              );
              this._store.dispatch(new SendCreateGroupFailure(err.error));
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $sendUpdateGroup = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SendUpdateGroupRequest>(
          EGroupsChangesActions.SendUpdateGroupRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService.update(action.id, action.data).pipe(
            map((group: IGroup) => {
              this._store.dispatch(new SendUpdateGroupSuccess());
              this._toastr.success(
                `Группа ${group.full_name} успешна изменена!`,
                '',
                {
                  timeOut: 3500,
                }
              );
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При изменении группы возникла ошибка'
                }`,
                'Что-то пошло не так',
                {
                  timeOut: 3500,
                }
              );
              this._store.dispatch(new SendUpdateGroupFailure(err.error));
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $delete = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DeleteGroupRequest>(EGroupsChangesActions.DeleteGroupRequest),
        switchMap(action => {
          return this._groupsChangesService.delete(action.id).pipe(
            map(() => {
              this._store.dispatch(new DeleteGroupSuccess());
              this._toastr.success(
                `Группа ${action.title} удалена успешно!`,
                '',
                {
                  timeOut: 3500,
                }
              );
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'При удалении группы возникла ошибка'
                }`,
                'Что-то пошло не так',
                {
                  timeOut: 3500,
                }
              );
              this._store.dispatch(new DeleteGroupFailure(err.error));
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $deactivateGroup = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SendDeactivateGroupRequest>(
          EGroupsChangesActions.SendDeactivateGroupRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService.deactivate(action.id).pipe(
            map((group: IGroup) => {
              this._toastr.success(
                `Группа ${group.full_name} успешно деактивирована!`,
                '',
                {
                  timeOut: 3500,
                }
              );
              this._store.dispatch(new SendDeactivateGroupSuccess());
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'Не получилось деактивировать группу'
                }`,
                `Что-то пошло не так`,
                {
                  timeOut: 3500,
                }
              );
              this._store.dispatch(new SendDeactivateGroupFailure(err.error));
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $activate = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<ActivateGroupRequest>(
          EGroupsChangesActions.ActivateGroupRequest
        ),
        switchMap(action => {
          return this._groupsChangesService.activate(action.id).pipe(
            map((group: IGroup) => {
              this._toastr.success(
                `Группа ${group.full_name} успешно активирована!`,
                '',
                {
                  timeOut: 3500,
                }
              );
              this._store.dispatch(new ActivateGroupSuccess());
            }),
            catchError(err => {
              this._toastr.error(
                `${
                  err?.error?.detail
                    ? err?.error?.detail
                    : 'Не получилось активировать группу'
                }`,
                `Что-то пошло не так`,
                {
                  timeOut: 3500,
                }
              );
              this._store.dispatch(new ActivateGroupFailure(err.error));
              return err.error;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  $transferStudents = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SendTransfersStudentsToGroupRequest>(
          EGroupsChangesActions.SendTransfersStudentsToGroupRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .studentsTransfer(action.id, {
              users: action.data.students,
              group: action.data.group,
            })
            .pipe(
              map(() => {
                this._toastr.success(`Студенты успешно переведены`, '', {
                  timeOut: 3500,
                });
                this._store.dispatch(new SendTransfersStudentsToGroupSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'Не удалось перевести студентов'
                  }`,
                  `Что-то пошло не так`,
                  {
                    timeOut: 3500,
                  }
                );

                this._store.dispatch(
                  new SendTransfersStudentsToGroupFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $addAdminsToTGGroup = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<AddAdminsToTGGroupRequest>(
          EGroupsChangesActions.AddAdminsToTGGroupRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .addAdminsToTGGroup(action.data)
            .pipe(
              map(() => {
                this._toastr.success(
                  `Админы успешно определены в группу Telegram`,
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new AddAdminsToTGGroupSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'Не удалось определить админов в группу Telegram'
                  }`,
                  `Не удалось определить админов в группу Telegram`,
                  {
                    timeOut: 3500,
                  }
                );

                this._store.dispatch(new AddAdminsToTGGroupFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $addStudents = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SendAddStudentsToGroupRequest>(
          EGroupsChangesActions.SendAddStudentsToGroupRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .addStudents(action.id, action.students)
            .pipe(
              map(() => {
                this._toastr.success(`Студенты успешно добавлены!`, '', {
                  timeOut: 3500,
                });
                this._store.dispatch(new SendAddStudentsToGroupSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'Не удалось перевести студентов'
                  }`,
                  `Что-то пошло не так`,
                  {
                    timeOut: 3500,
                  }
                );

                this._store.dispatch(
                  new SendAddStudentsToGroupFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $addTeachers = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SendAddTeachersToGroupRequest>(
          EGroupsChangesActions.SendAddTeachersToGroupRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .addTeachers(action.id, action.teachers)
            .pipe(
              map(() => {
                this._toastr.success(`Преподаватели успешно добавлены!`, '', {
                  timeOut: 3500,
                });
                this._store.dispatch(new SendAddTeachersToGroupSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'Не удалось перевести студентов'
                  }`,
                  `Что-то пошло не так`,
                  {
                    timeOut: 3500,
                  }
                );

                this._store.dispatch(
                  new SendAddTeachersToGroupFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $addCheckingTeachers = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<AddCheckingTeacherRequest>(
          EGroupsChangesActions.AddCheckingTeacherRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .addCheckingTeacher(action.data)
            .pipe(
              map(() => {
                this._toastr.success(
                  `Преподаватель успешно добавлен в список проверяющих!`,
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new AddCheckingTeacherSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'Не удалось переместить преподавателя в список проверяющих'
                  }`,
                  `Что-то пошло не так`,

                  {
                    timeOut: 3500,
                  }
                );

                this._store.dispatch(new AddCheckingTeacherFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $refreshCheckingTeachersHomeworks = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<RefreshCheckingTeacherHomeworksRequest>(
          EGroupsChangesActions.RefreshCheckingTeacherHomeworksRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .refreshCheckingTeacherHomeworks(action.group_id, action.data)
            .pipe(
              map(() => {
                this._toastr.success(
                  `Домашнии работы успешно распределены между проверяющими`,
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(
                  new RefreshCheckingTeacherHomeworksSuccess()
                );
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'Не удалось распределить домашнии работы'
                  }`,
                  `Что-то пошло не так`,
                  {
                    timeOut: 3500,
                  }
                );
                this._toastr.error(err.error.non_fields_error[0], '', {
                  timeOut: 3500,
                });
                this._store.dispatch(
                  new RefreshCheckingTeacherHomeworksFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $deleteCheckingTeachers = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DeleteCheckingTeacherRequest>(
          EGroupsChangesActions.DeleteCheckingTeacherRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .deleteCheckingTeacher(action.teacher_id)
            .pipe(
              map(() => {
                this._toastr.success(
                  `Преподаватель успешно удален из списка проверяющих!`,
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new DeleteCheckingTeacherSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'Не удалось удалить преподавателя из списока проверяющих'
                  }`,
                  `Что-то пошло не так`,

                  {
                    timeOut: 3500,
                  }
                );

                this._store.dispatch(
                  new DeleteCheckingTeacherFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $dropoutStudent = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SendStudentDropoutFromGroupRequest>(
          EGroupsChangesActions.SendStudentDropoutFromGroupRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .dropoutStudent(action.id, action.students)
            .pipe(
              map(() => {
                this._toastr.success(
                  'Отчисление студента прошло успешно!',
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new SendStudentDropoutFromGroupSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При отличнии студента возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(
                  new SendStudentDropoutFromGroupFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $activateStudent = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SendActivateStudentToGroupRequest>(
          EGroupsChangesActions.SendActivateStudentToGroupRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .activateStudent(action.id, action.students)
            .pipe(
              map(() => {
                this._toastr.success('Активация прошла успешно!', '', {
                  timeOut: 3500,
                });
                this._store.dispatch(new SendActivateStudentToGroupSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При активации возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(
                  new SendActivateStudentToGroupFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $setOnlineStudent = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<SetStudentOnlineInGroupRequest>(
          EGroupsChangesActions.SetStudentOnlineRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .setStudentOnline(action.groupId, action.studentId)
            .pipe(
              map(() => {
                this._toastr.success(
                  'Изменение формата обучения прошла успешно!',
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new SetStudentOnlineInGroupSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При изменения формата обучения возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(
                  new SetStudentOnlineInGroupFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $deleteTeachers = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DeleteTeacherFromGroupRequest>(
          EGroupsChangesActions.DeleteTeacherRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .deleteTeachers(action.id, action.teachers)
            .pipe(
              map(() => {
                this._toastr.success(`Удаление прошло успешно!`, '', {
                  timeOut: 3500,
                });
                this._store.dispatch(new DeleteTeacherFromGroupSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При попытке удаления возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(
                  new DeleteTeacherFromGroupFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $returnTeacher = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<ReturnTeacherRequest>(
          EGroupsChangesActions.ReturnTeacherRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .returnTeacher(action.group_id, action.users)
            .pipe(
              map(() => {
                this._toastr.success(
                  `Преподаватель был успешно заново добавлен в группу!`,
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new ReturnTeacherSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При попытке возвращения преподавателя возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new ReturnTeacherFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $deleteTeacherFromSelectedGroup = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DeleteTeacherFromSelectedGroupsRequest>(
          EGroupsChangesActions.DeleteTeacherFromSelectedGroupsRequest
        ),
        map(action => action),
        switchMap(action => {
          return this._groupsChangesService
            .deleteTeacherFromSelectedGroups(action.groups_id, action.teacher)
            .pipe(
              map(() => {
                this._toastr.success(`Удаление прошло успешно!`, '', {
                  timeOut: 3500,
                });
                this._store.dispatch(
                  new DeleteTeacherFromSelectedGroupsSuccess()
                );
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При попытке удаления возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(
                  new DeleteTeacherFromSelectedGroupsFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $editStudentAccess = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<EditStudentAccessRequest>(
          EGroupsChangesActions.EditStudentAccessRequest
        ),
        switchMap(action => {
          return this._groupsChangesService
            .editStudentAccess(action.id, action.data)
            .pipe(
              map(() => {
                this._toastr.success(
                  `Настройки доступа изменены успешно!`,
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new EditStudentAccessSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При изминении настроек доступа возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new EditStudentAccessFailure(err.error));
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $transferToESDP = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<TransferToESDPRequest>(
          EGroupsChangesActions.TransferToESDPRequest
        ),
        switchMap(action => {
          return this._groupsChangesService
            .transferToESDP(action.groupId, action.data)
            .pipe(
              map(() => {
                this._toastr.success(
                  'Группа успешно переведена на ESDP. Теперь вам нужно сгенерировать команды'
                );
                this._router.navigate([`/esdp/generate-teams`], {
                  queryParams: {
                    course: action.courseId,
                    group: action.groupId,
                  },
                });
                this._store.dispatch(new TransferToESDPSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При переводе группы на ESDP возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new TransferToESDPFailure(err.error));
                return err.statusText;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $studentHappyPointAllowed = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<GroupsStudentHappyPointAllowedRequest>(
          EGroupsChangesActions.StudentHappyPointAllowedRequest
        ),
        switchMap(action => {
          return this._groupsChangesService
            .studentHappyPointAllowed(action.groupId, action.data)
            .pipe(
              map(() => {
                this._toastr.success(`Happy Point указан успешно!`);
                this._store.dispatch(
                  new GroupsStudentHappyPointAllowedSuccess()
                );
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При указании HP возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(
                  new GroupsStudentHappyPointAllowedFailure()
                );
                return err.statusText;
              })
            );
        })
      );
    },
    { dispatch: false }
  );

  $deleteTeacherFrom = createEffect(
    () => {
      return this._actions$.pipe(
        ofType<DeleteTeacherRequest>(
          EGroupsChangesActions.DeleteTeacherFromGroupRequest
        ),
        switchMap(action => {
          return this._groupsChangesService
            .deleteTeacherFromGroup(action.groupId, action.user)
            .pipe(
              map(() => {
                this._toastr.success(
                  `Доступ к группе для преподавателя закрыт`,
                  '',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(new DeleteTeacherFromGroupSuccess());
              }),
              catchError(err => {
                this._toastr.error(
                  `${
                    err?.error?.detail
                      ? err?.error?.detail
                      : 'При закрытии доступа возникла ошибка'
                  }`,
                  'Что-то пошло не так',
                  {
                    timeOut: 3500,
                  }
                );
                this._store.dispatch(
                  new DeleteCheckingTeacherFailure(err.error)
                );
                return err.error;
              })
            );
        })
      );
    },
    { dispatch: false }
  );
}
